import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaInbox, FaEnvelopeOpenText } from 'react-icons/fa';
import ConfirmationModal from '../ConfirmationModal';
import EmailThreadPanel from './EmailThreadPanel';
import {
  listMonitoredEmails,
  sendReplies,
} from '../../services/emailMonitorService';

// Inbound support mail across the monitored inboxes (ticket 75).
//
// Laid out as list-plus-thread, matching ConversationsTab next door: a support thread can run long,
// and the open email lives in the URL so back/forward and a shared link both work.
//
// The page owns the header row (title + settings + "Check mail") and the banner above this;
// this component owns the full-width filter bar and the list-plus-thread card below it.
// "Check mail" makes the API poll Microsoft right now and takes a few seconds, while the list
// here only re-reads what Loope already knows, which is instant.

const STATUS_PARAM = {
  actionable: ['classified', 'drafted', 'needs_review'],
  sent: 'sent',
  skipped: ['skipped_internal', 'labelled_unknown'],
  all: null,
};

const STATUS_META = {
  classified: { label: 'To answer', className: 'bg-cyan-100 text-cyan-800' },
  drafted: { label: 'Draft ready', className: 'bg-blue-100 text-blue-800' },
  needs_review: { label: 'Needs review', className: 'bg-amber-100 text-amber-800' },
  sent: { label: 'Replied', className: 'bg-green-100 text-green-800' },
  skipped_internal: { label: 'Internal', className: 'bg-gray-100 text-gray-600' },
  labelled_unknown: { label: 'Unknown sender', className: 'bg-gray-100 text-gray-600' },
  gift_voucher_rcti: { label: 'RCTI created', className: 'bg-green-100 text-green-800' },
};

// Gift voucher emails are never answered, so their badge says what happened to the RCTI instead.
export const voucherOutcomeMeta = (gv = {}) => {
  if (gv.outcome === 'needs_person') return { label: 'Needs a person', className: 'bg-amber-100 text-amber-800' };
  if (gv.outcome === 'handled_by_hand') return { label: 'Done by hand', className: 'bg-green-100 text-green-800' };
  const label = gv.rctiNumber ? `RCTI created: ${gv.rctiNumber}` : 'RCTI created';
  if (gv.problems?.length) return { label: `${label}, check it`, className: 'bg-amber-100 text-amber-800' };
  return { label, className: 'bg-green-100 text-green-800' };
};

// Two orthogonal filters: who sent it × what it is about. Either, both, or neither
// can narrow the list — e.g. suppliers + issues, or issues regardless of sender.
const SENDER_META = {
  franchisee: { label: 'Franchisee', className: 'bg-indigo-100 text-indigo-800' },
  supplier: { label: 'Supplier', className: 'bg-purple-100 text-purple-800' },
  other: { label: 'Other sender', className: 'bg-gray-100 text-gray-600' },
};

const ISSUE_META = {
  issue: { label: 'Issue', className: 'bg-red-100 text-red-800' },
  enquiry: { label: 'Enquiry', className: 'bg-blue-100 text-blue-800' },
  other: { label: 'Unclear', className: 'bg-gray-100 text-gray-600' },
};

const SENDER_FILTERS = [
  { key: '', label: 'All senders' },
  { key: 'franchisee', label: 'Franchisees' },
  { key: 'supplier', label: 'Suppliers' },
  { key: 'other', label: 'Other senders' },
];

const TYPE_FILTERS = [
  { key: 'issue', label: 'Issues' },
  { key: 'enquiry', label: 'Enquiries' },
  { key: 'other', label: 'Other' },
];

// Same fallback as the API (senderGroupOfDoc / matchesSenderGroup): stored fields win,
// older records derive from userType / intent, so badges work before any backfill.
// Unverified guesses group with their guessed side but are always labelled as guesses.
const senderOf = (email) => {
  if (email.senderGroup === 'franchisee' || email.senderGroup === 'supplier') {
    return { group: email.senderGroup, verified: true };
  }
  if (email.userType === 'franchisee') return { group: 'franchisee', verified: true };
  if (email.userType === 'client') return { group: 'supplier', verified: true };
  const guess = email.senderGuess?.group === 'franchisee' || email.senderGuess?.group === 'supplier'
    ? email.senderGuess.group
    : 'other';
  return { group: guess, verified: false };
};

const ISSUE_INTENTS = new Set(['technical_or_data_issue', 'account_or_access']);
const typeOf = (email) => {
  if (typeof email.isIssue === 'boolean') return email.isIssue ? 'issue' : 'enquiry';
  if (!email.intent) return 'other';
  return ISSUE_INTENTS.has(email.intent) ? 'issue' : 'enquiry';
};

const STATUS_FILTERS = [
  { key: 'actionable', label: 'To answer' },
  { key: 'sent', label: 'Replied' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'all', label: 'All' },
];

// A mailbox is a long address; the part before the @ is what tells two inboxes apart at a glance.
const mailboxLabel = (address) => String(address || '').split('@')[0] || address || '—';

// Time for today, date for anything older — the same shorthand a mail client uses.
const listWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const EmailMonitorTab = ({ refreshToken = 0, mailboxes = [], lookbackHours = 24, notify }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('e');

  const [emails, setEmails] = useState([]);
  const [enabled, setEnabled] = useState(true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('actionable');
  const [mailboxFilter, setMailboxFilter] = useState('');
  const [senderFilter, setSenderFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [pendingSend, setPendingSend] = useState(null);
  const [sending, setSending] = useState(false);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Deliberately no time filter. The monitor already refuses to ingest anything older than the
      // lookback window, so age is bounded at the source — filtering again here would hide an email
      // nobody got round to answering yesterday, which is precisely the one that still needs answering.
      const { enabled: isEnabled, emails: list } = await listMonitoredEmails({
        status: STATUS_PARAM[statusFilter],
        mailbox: mailboxFilter || null,
        senderGroup: senderFilter || null,
        issueFlag: typeFilter || null,
      });
      setEnabled(isEnabled);
      setEmails(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || 'Failed to load monitored emails.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, mailboxFilter, senderFilter, typeFilter]);

  // refreshToken is bumped by the page's "Check mail" button so the list re-reads
  // what the poll just found.
  useEffect(() => { fetchEmails(); }, [fetchEmails, refreshToken]);

  const selectEmail = useCallback((id) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (id) params.set('e', id);
      else params.delete('e');
      return params;
    });
  }, [setSearchParams]);

  // Fall back to the mailboxes present in the data when the settings call failed.
  const mailboxOptions = mailboxes.length > 0
    ? mailboxes
    : [...new Set(emails.map((e) => e.mailbox).filter(Boolean))];

  const selected = emails.find((e) => e.id === selectedId) || null;

  // A filter change can drop the open email out of the list; closing it beats leaving a stale thread.
  useEffect(() => {
    if (selectedId && !loading && !emails.some((e) => e.id === selectedId)) selectEmail(null);
  }, [selectedId, emails, loading, selectEmail]);

  const confirmSend = async () => {
    if (!pendingSend) return;
    setSending(true);
    try {
      const res = await sendReplies([pendingSend.email.id]);
      const failed = (res?.results || []).filter((r) => !r.ok);
      notify?.(failed.length === 0
        ? { type: 'success', text: 'Reply sent.' }
        : { type: 'error', text: `Reply not sent: ${failed.map((f) => f.error).join('; ')}` });
      await fetchEmails();
    } catch (e) {
      notify?.({ type: 'error', text: e.message || 'Failed to send the reply.' });
    } finally {
      setSending(false);
      setPendingSend(null);
    }
  };

  const selectClass = 'text-sm border border-gray-300 rounded px-2 py-1.5 focus:ring-jims-cyan focus:border-jims-cyan';

  return (
    <div>
      {!enabled && !loading && (
        <div className="mb-4 px-4 py-3 rounded border bg-amber-50 border-amber-200 text-amber-800">
          Email monitoring is currently <b>switched off</b> on the server, so no mail is being read and replies cannot be sent.
        </div>
      )}

      {error && <div className="mb-4 px-4 py-3 rounded border bg-red-50 border-red-200 text-red-800">{error}</div>}

      {/* Narrow the list before it loads: mailbox, who sent it, what it is about.
          Sized to content, not full width — a stretched bar looks lost on a wide screen. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="sr-only" htmlFor="mailbox-filter">Mailbox</label>
        <select id="mailbox-filter" value={mailboxFilter} onChange={(e) => setMailboxFilter(e.target.value)} className={`${selectClass} w-auto bg-white`}>
          <option value="">All mailboxes</option>
          {mailboxOptions.map((m) => <option key={m} value={m}>{mailboxLabel(m)}</option>)}
        </select>

        <label className="sr-only" htmlFor="sender-filter">Sender</label>
        <select id="sender-filter" value={senderFilter} onChange={(e) => setSenderFilter(e.target.value)} className={`${selectClass} w-auto bg-white`}>
          {SENDER_FILTERS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>

        {/* Type switches as pills: one tap, and tapping the active one clears back to all. */}
        <div className="flex items-center gap-1.5" role="group" aria-label="Type">
          {TYPE_FILTERS.map((t) => {
            const active = typeFilter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                aria-pressed={active}
                onClick={() => setTypeFilter(active ? '' : t.key)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan ${
                  active
                    ? 'bg-jims-cyan text-white border-jims-cyan'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Two panes on desktop; on mobile the thread replaces the list so neither is cramped.
            Tall on purpose: the standalone Email Monitor page has little chrome above this. */}
        <div className="flex h-[calc(100vh-13rem)] min-h-[32rem]">
          <aside
            className={`w-full md:w-96 lg:w-[28rem] shrink-0 border-r border-gray-200 flex-col bg-gray-50 ${
              selected ? 'hidden md:flex' : 'flex'
            }`}
            aria-label="Monitored emails"
          >
            <div className="px-3 py-2 border-b border-gray-200 bg-white">
              <div className="inline-flex rounded-md w-full" role="group" aria-label="Status">
                {STATUS_FILTERS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setStatusFilter(tab.key)}
                    className={`flex-1 px-2 py-1.5 text-sm font-medium border border-gray-200 first:rounded-l-md last:rounded-r-md ${
                      statusFilter === tab.key ? 'bg-jims-cyan text-white border-jims-cyan' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <p className="px-4 py-8 text-center text-sm text-gray-500">Loading&hellip;</p>
              ) : emails.length === 0 ? (
                <div className="px-4 py-10 text-center text-gray-500">
                  <FaInbox className="mx-auto text-3xl text-gray-300 mb-2" />
                  <p className="text-sm">No emails match these filters.</p>
                  {enabled && <p className="text-xs mt-1">Use <b>Check mail</b> to look at the inboxes now.</p>}
                </div>
              ) : (
                <ul>
                  {emails.map((email) => {
                    const meta = STATUS_META[email.status] || { label: email.status || '—', className: 'bg-gray-100 text-gray-600' };
                    const sender = senderOf(email);
                    const senderMeta = SENDER_META[sender.group];
                    const type = ISSUE_META[typeOf(email)];
                    const isOpen = email.id === selectedId;
                    const voucher = email.giftVoucher ? voucherOutcomeMeta(email.giftVoucher) : null;
                    return (
                      <li key={email.id}>
                        <button
                          type="button"
                          onClick={() => selectEmail(email.id)}
                          aria-current={isOpen}
                          className={`w-full text-left px-3 py-3 border-b border-gray-200 transition-colors ${
                            isOpen ? 'bg-white border-l-4 border-l-jims-cyan' : 'hover:bg-white'
                          }`}
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">{email.senderName || email.from}</span>
                            <span className="text-xs text-gray-500 shrink-0">{listWhen(email.receivedDateTime)}</span>
                          </div>
                          <p className="text-sm text-gray-700 truncate">{email.subject || '(no subject)'}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {voucher && (
                              <>
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-800">Gift voucher</span>
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${voucher.className}`}>{voucher.label}</span>
                              </>
                            )}
                            {!voucher && senderMeta && (
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${senderMeta.className}`}>{senderMeta.label}</span>
                            )}
                            {!voucher && !sender.verified && sender.group !== 'other' && (
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title="Not found in Loope — allow-list this sender if valid.">Not in Loope</span>
                            )}
                            {!voucher && type && (
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${type.className}`}>{type.label}</span>
                            )}
                            {!voucher && <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.className}`}>{meta.label}</span>}
                            <span className="text-[11px] text-gray-400 truncate" title={email.mailbox}>{mailboxLabel(email.mailbox)}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          <section className={`flex-1 flex-col min-w-0 ${selected ? 'flex' : 'hidden md:flex'}`} aria-label="Email thread">
            {!selected ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-gray-500">
                <FaEnvelopeOpenText className="h-10 w-10 text-gray-300 mb-3" />
                <p className="font-medium text-gray-600">Select an email</p>
                <p className="text-sm mt-1">
                  Only mail from the past {lookbackHours === 24 ? 'day' : `${lookbackHours} hours`} is picked up,
                  and nothing is answered automatically.
                </p>
              </div>
            ) : (
              <EmailThreadPanel
                key={selected.id}
                email={selected}
                sending={sending && pendingSend?.email.id === selected.id}
                onSend={(e, text) => setPendingSend({ email: e, text })}
                onBack={() => selectEmail(null)}
                onChanged={fetchEmails}
                notify={notify}
              />
            )}
          </section>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!pendingSend}
        onClose={() => setPendingSend(null)}
        onConfirm={confirmSend}
        title="Send this reply?"
        message={pendingSend
          ? `Send your reply to ${pendingSend.email.senderName || pendingSend.email.from}? It goes out from ${pendingSend.email.mailbox} and cannot be unsent.`
          : ''}
        isLoading={sending}
        loadingText="Sending…"
        confirmText="Send now"
        variant="primary"
      />
    </div>
  );
};

export default EmailMonitorTab;
